// components/CrewEfficiencyDashboard.js
import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Button,
  TextField
} from '@material-ui/core';
import {
  Dashboard,
  Assessment,
  Map,
  GetApp,
  Print,
  Refresh,
  History,
  NavigateBefore,
  NavigateNext,
  CalendarToday
} from '@material-ui/icons';
import { DatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

// Import sub-components
import EfficiencyOverviewPanel from './EfficiencyOverviewPanel';
import WorkloadBalanceGrid from './WorkloadBalanceGrid';
import GeographicEfficiencyView from './GeographicEfficiencyView';
import RecommendationsPanel from './RecommendationsPanel';
import RecommendationActionDialog from './RecommendationActionDialog';
import ScheduleHistoryViewer from './ScheduleHistoryViewer';
import { useCrewEfficiency } from '../hooks/useCrewEfficiency';
import { scheduleHistory, recordJobTransfer } from '../utils/scheduleHistory';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  tabPanel: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2)
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1)
  }
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`efficiency-tabpanel-${index}`}
      aria-labelledby={`efficiency-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box height="100%">
          {children}
        </Box>
      )}
    </div>
  );
}

const CrewEfficiencyDashboard = ({ 
  weeklyRoutes, 
  crews, 
  selectedDate,
  onCrewSelect,
  onDateChange,
  sites,
  handleAssignToCrew,
  handleUnassignFromCrew,
  setViewMode,
  dispatch
}) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, recommendation: null });
  const [historyOpen, setHistoryOpen] = useState(false);

  // Use the efficiency hook to get all metrics
  const { crewMetrics, rankings, insights, summary, recommendations } = useCrewEfficiency(
    weeklyRoutes,
    crews,
    selectedDate
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExportCSV = () => {
    // TODO: Implement CSV export
    console.log('Exporting CSV...');
    handleExportClose();
  };

  const handlePrint = () => {
    // TODO: Implement print functionality
    window.print();
  };

  const handleRefresh = () => {
    // Force re-fetch of data
    if (onDateChange) {
      onDateChange(selectedDate);
    }
  };

  const handleExecuteAction = async (action) => {
    console.log('Executing action:', action);
    
    // Handle the actual job transfers based on action type
    if (action.type === 'workload_rebalance' && action.jobs && action.jobs.length > 0) {
      try {
        // Record the change in history BEFORE making changes
        const fromCrew = crews.find(c => c._id === action.fromCrew);
        const toCrew = crews.find(c => c._id === action.toCrew);
        
        recordJobTransfer(
          fromCrew,
          toCrew,
          action.jobs,
          action.impact
        );
        
        // Transfer each job from one crew to another
        for (const job of action.jobs) {
          // First unassign from current crew
          if (handleUnassignFromCrew) {
            await handleUnassignFromCrew(job, { _id: action.fromCrew }, selectedDate);
          }
          
          // Then assign to new crew
          if (handleAssignToCrew) {
            await handleAssignToCrew(job, { _id: action.toCrew }, selectedDate);
          }
        }
        
        // Refresh the view
        if (onDateChange) {
          onDateChange(selectedDate);
        }
        
        // Show success notification
        if (dispatch) {
          dispatch({
            type: 'SHOW_NOTIFICATION',
            payload: {
              message: `Successfully transferred ${action.jobs.length} jobs`,
              severity: 'success'
            }
          });
        }
      } catch (error) {
        console.error('Error executing action:', error);
        throw error;
      }
    }
  };

  const handleDayClick = async (crew, day) => {
    // Switch to map view first
    if (setViewMode) {
      setViewMode('map');
    }
    
    // Navigate to specific day and crew
    const targetDate = moment(selectedDate).day(day).toDate();
    
    // Small delay to ensure view mode changes first
    setTimeout(() => {
      if (onDateChange) {
        onDateChange(targetDate);
      }
      if (onCrewSelect) {
        const crewData = crews.find(c => c._id === crew.crewId);
        if (crewData) {
          onCrewSelect(crewData);
        }
      }
    }, 100);
  };

  const handleCrewClick = (crewMetric) => {
    // Navigate to crew details
    const crewData = crews.find(c => c._id === crewMetric.crewId);
    if (crewData && onCrewSelect) {
      onCrewSelect(crewData);
    }
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <Typography variant="h5">
              Crew Efficiency Dashboard
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Week of {moment(selectedDate).startOf('week').format('MMMM D, YYYY')}
            </Typography>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton
              size="small"
              onClick={() => onDateChange && onDateChange(moment(selectedDate).subtract(1, 'week').toDate())}
              title="Previous Week"
            >
              <NavigateBefore />
            </IconButton>
            
            <DatePicker
              value={selectedDate}
              onChange={(date) => onDateChange && onDateChange(date)}
              format="MMM dd, yyyy"
              variant="inline"
              inputVariant="outlined"
              size="small"
              style={{ width: '140px' }}
              TextFieldComponent={(props) => (
                <TextField
                  {...props}
                  size="small"
                  variant="outlined"
                  InputProps={{
                    ...props.InputProps,
                    endAdornment: <CalendarToday fontSize="small" />
                  }}
                />
              )}
            />
            
            <IconButton
              size="small"
              onClick={() => onDateChange && onDateChange(moment(selectedDate).add(1, 'week').toDate())}
              title="Next Week"
            >
              <NavigateNext />
            </IconButton>
          </div>
        </div>
        
        <div className={classes.actionButtons}>
          <Button
            startIcon={<History />}
            onClick={() => setHistoryOpen(true)}
            size="small"
          >
            History
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            size="small"
          >
            Refresh
          </Button>
          <Button
            startIcon={<Print />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
          <Button
            startIcon={<GetApp />}
            onClick={handleExportClick}
            size="small"
            variant="outlined"
          >
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Paper square className={classes.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab 
            icon={<Dashboard />} 
            label="Overview" 
            id="efficiency-tab-0"
          />
          <Tab 
            icon={<Assessment />} 
            label="Workload Balance" 
            id="efficiency-tab-1"
          />
          <Tab 
            icon={<Map />} 
            label="Geographic Analysis" 
            id="efficiency-tab-2"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <div className={classes.tabPanel}>
        <TabPanel value={tabValue} index={0}>
          <Box height="100%" display="flex" flexDirection="column">
            <Box flex={1} overflow="auto">
              <EfficiencyOverviewPanel
                crewMetrics={crewMetrics}
                rankings={rankings}
                insights={insights}
                summary={summary}
                onCrewClick={(crew) => {
                  // Switch to map view
                  if (setViewMode) {
                    setViewMode('map');
                  }
                  // Select the crew
                  const crewData = crews.find(c => c._id === crew.crewId);
                  if (crewData && onCrewSelect) {
                    onCrewSelect(crewData);
                  }
                }}
              />
              <RecommendationsPanel
                recommendations={recommendations}
                onActionClick={(rec, action) => {
                  if (action === 'execute') {
                    setActionDialog({ open: true, recommendation: rec });
                  } else if (action === 'details') {
                    // Switch to map view and highlight affected crews
                    if (setViewMode) {
                      setViewMode('map');
                    }
                    // Select first affected crew if available
                    if (rec.affectedCrews && rec.affectedCrews.length > 0) {
                      const firstCrewName = rec.affectedCrews[0];
                      const crew = crews.find(c => c.name === firstCrewName);
                      if (crew && onCrewSelect) {
                        onCrewSelect(crew);
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <WorkloadBalanceGrid
            crewMetrics={crewMetrics}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <GeographicEfficiencyView
            crewMetrics={crewMetrics}
            selectedDate={selectedDate}
            onCrewClick={handleCrewClick}
            weeklyRoutes={weeklyRoutes}
            sites={sites}
          />
        </TabPanel>
      </div>

      {/* Export Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={handleExportCSV}>
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleExportClose}>
          Export as PDF
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleExportClose}>
          Email Report
        </MenuItem>
      </Menu>

      {/* Action Dialog */}
      <RecommendationActionDialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, recommendation: null })}
        recommendation={actionDialog.recommendation}
        crewMetrics={crewMetrics}
        weeklyRoutes={weeklyRoutes}
        onExecuteAction={handleExecuteAction}
        sites={sites}
        crews={crews}
      />

      {/* History Viewer */}
      <ScheduleHistoryViewer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onRestore={async (restoredState, historyItem) => {
          console.log('Restoring to:', historyItem);
          // Here you would apply the restored state to your application
          // This would typically involve updating your Redux state or calling APIs
          if (onDateChange) {
            onDateChange(selectedDate); // Refresh current view
          }
          setHistoryOpen(false);
        }}
        currentState={{
          weeklyRoutes,
          crews,
          sites,
          selectedDate
        }}
      />
    </div>
  );
};

export default CrewEfficiencyDashboard;